/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import StatusDiscoveryIcon from '@atlaskit/icon/core/status-discovery';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInfoIcon from '@atlaskit/icon/core/status-information';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import TaskToDo from '@atlaskit/icon/core/task-to-do';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import messages from './messages';

export type LoadingStatus =
	| 'loading'
	| 'success'
	| 'error'
	| 'info'
	| 'warning'
	| 'discovery'
	| 'to-do';

export const StatusIndicator = ({
	status,
	testId,
	size = 'medium',
}: {
	status: LoadingStatus;
	testId?: string;
	size?: 'small' | 'medium';
}): JSX.Element | undefined => {
	const { formatMessage } = useIntl();

	switch (status) {
		case 'loading':
			return <Spinner size="small" label={formatMessage(messages.loading)} testId={testId} />;
		case 'success':
			return (
				<StatusSuccessIcon
					label={formatMessage(messages.success)}
					size={size}
					color={token('color.icon.success')}
					testId={testId}
				/>
			);
		case 'info':
			return (
				<StatusInfoIcon
					label={formatMessage(messages.info)}
					size={size}
					color={token('color.text.information')}
					testId={testId}
				/>
			);
		case 'error':
			return (
				<StatusErrorIcon
					label={formatMessage(messages.error)}
					size={size}
					color={token('color.text.danger')}
					testId={testId}
				/>
			);
		case 'warning':
			return (
				<StatusWarningIcon
					label={formatMessage(messages.warning)}
					size={size}
					color={token('color.icon.warning')}
					testId={testId}
				/>
			);
		case 'discovery':
			return (
				<StatusDiscoveryIcon
					label={formatMessage(messages.discovery)}
					size={size}
					color={token('color.icon.discovery')}
					testId={testId}
				/>
			);
		case 'to-do':
			return <TaskToDo label={formatMessage(messages.todo)} size={size} testId={testId} />;
		// explicitly return nothing from default case.
		// This shouldn't happen since `status` is required, but it could if someone casts incorrectly or data is undefined from the server.
		default:
	}
};
